import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { PATCH_URL } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import {
  SetPriorityRequest,
  SetPriorityResponse
} from '@utils/types/back-office/priority-queue';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type SetItemPriorityParams = {
  itemId: string;
  payload: SetPriorityRequest;
};

export const useSetItemPriority = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const setItemPriority = async ({
    itemId,
    payload
  }: SetItemPriorityParams) => {
    const { data } = await Axios.patch<SetPriorityResponse>(
      PATCH_URL(itemId).setPriorityItem,
      payload
    );
    return data;
  };

  return useMutation<
    SetPriorityResponse,
    AxiosError<BaseError>,
    SetItemPriorityParams
  >(setItemPriority, {
    onSuccess: data => {
      toast.success(data.message || t('priority-updated'));
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    },
    onError: (error: AxiosError<BaseError>) => {
      const d = error.response?.data as Record<string, unknown>;
      const msg =
        (d?.error_description as string) ||
        (d?.detail as string) ||
        (d?.error as string) ||
        (d?.message as string) ||
        t('an-error-occurred');
      toast.error(String(msg));
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    }
  });
};
